export const AddLabelStatus = Object.freeze({
    SUCCESS: "SUCCESS",
    CSRF_TOKEN_ERROR: "CSRF_TOKEN_ERROR",
    CSRF_FETCH_ERROR: "CSRF_FETCH_ERROR",
    LABEL_ADD_ERROR: "LABEL_ADD_ERROR",
    CORS_ERROR: "CORS_ERROR",
    NETWORK_ERROR: "NETWORK_ERROR",
    UNKNOWN_ERROR: "UNKNOWN_ERROR",
});

// Updated function using MediaWiki Action API with OAuth 2.0
export async function addLabel(accessToken, nodeId, label, ietf) {
    // Determine API URL based on environment - Use Action API with OAuth
    const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';
    
    // SIMPLIFIED: Nginx (Prod) and setupProxy.js (Dev) both handle this path now.
    const apiUrl = "/w/api.php"; 

    console.log(`Wikidata Action API: Using endpoint: ${apiUrl}`);
    console.log(`Adding label '${label}' to entity ${nodeId} in language ${ietf}`);

    try {
        // Step 1: Get CSRF token using OAuth authentication
        const tokenUrl = `${apiUrl}?action=query&meta=tokens&format=json`;

        console.log(`Fetching CSRF token from: ${tokenUrl}`);

        const tokenResponse = await fetch(tokenUrl, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
            },
            credentials: isProduction ? "omit" : "include",
            mode: "cors"
        });

        console.log(`Token Response Status: ${tokenResponse.status} ${tokenResponse.statusText}`);

        if (!tokenResponse.ok) {
            const errorText = await tokenResponse.text();
            console.error(`Failed to retrieve CSRF token. Status: ${tokenResponse.status}, Response: ${errorText}`);
            return AddLabelStatus.CSRF_FETCH_ERROR;
        }

        const tokenData = await tokenResponse.json();
        console.log("Token Response Data:", tokenData);
        const csrfToken = tokenData?.query?.tokens?.csrftoken;

        if (!csrfToken) {
            console.error("No CSRF token retrieved from response:", tokenData);
            if (tokenData?.error) {
                console.error("Wikidata API Error:", tokenData.error);

                // Check for specific OAuth errors
                if (tokenData.error.code === 'mwoauth-invalid-authorization') {
                    console.error("OAuth Error: Invalid access token. The token may be expired, invalid, or have insufficient permissions.");
                    console.error("Please generate a new OAuth 2.0 access token with 'edit' permissions.");
                    return AddLabelStatus.CSRF_TOKEN_ERROR;
                }
            }
            return AddLabelStatus.CSRF_TOKEN_ERROR;
        }

        console.log("Successfully retrieved CSRF token");

        // Step 2: Add label using wbsetlabel action
        const labelPayload = new URLSearchParams({
            action: "wbsetlabel",
            id: nodeId,
            language: ietf,
            value: label,
            token: csrfToken,
            format: "json"
        });

        console.log(`Uploading label '${label}' to entity ${nodeId} in language ${ietf}`);

        const labelResponse = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Authorization": `Bearer ${accessToken}`,
                "User-Agent": "EndoLink/1.0 (https://endo-link.ivia.ch/)",
                ...(isProduction ? {} : {
                    "Origin": "http://localhost:3000"
                })
            },
            body: labelPayload,
            credentials: isProduction ? "omit" : "include",
            mode: "cors" 
        });

        console.log(`Label Upload Response Status: ${labelResponse.status} ${labelResponse.statusText}`);

        if (labelResponse.ok) {
            const responseData = await labelResponse.json();
            console.log(`Successfully added label '${label}' to entity ${nodeId} in language ${ietf}`);
            console.log("Upload Response Data:", responseData);
            return AddLabelStatus.SUCCESS;
        } else {
            const errorText = await labelResponse.text();
            console.error(`Failed to add label. Status: ${labelResponse.status}, Response: ${errorText}`);
            console.error(`Payload sent:`, Object.fromEntries(labelPayload));

            // Parse error details if available
            try {
                const errorData = JSON.parse(errorText);
                console.error("Parsed error details:", errorData);

                if (errorData.error?.info) {
                    console.error("Error message:", errorData.error.info);
                }
            } catch (parseError) {
                console.error("Could not parse error response as JSON");
            }

            return AddLabelStatus.LABEL_ADD_ERROR;
        }
    } catch (error) {
        console.error("An error occurred during Wikidata upload:", error);

        // Categorize different types of errors for better user feedback
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            console.error("Network error - CORS or connectivity issue");
            if (isProduction) {
                console.error("PRODUCTION ERROR: Issue with CORS proxy or Wikidata API connectivity");
                console.error("Using CORS proxy:", apiUrl);
                return AddLabelStatus.CORS_ERROR;
            } else {
                console.error("DEVELOPMENT NETWORK ERROR: Check if Wikidata proxy is working");
                return AddLabelStatus.NETWORK_ERROR;
            }
        } else if (error.name === 'SyntaxError') {
            console.error("Response parsing error - invalid JSON from Wikidata API");
            return AddLabelStatus.CSRF_FETCH_ERROR;
        } else {
            console.error("Unknown error type:", error.name, error.message);
            return AddLabelStatus.UNKNOWN_ERROR;
        }
    }
}
